import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { MenuItem } from 'primeng/api/menuitem';
import { isPlatformBrowser } from '@angular/common';
import { HomeService } from 'src/app/pages/home/services/home.service';
import { environment } from 'src/environments/environment';
import { CurrencySelectionService } from 'src/app/services/currency/currency-selection.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AppModeService } from 'src/app/services/app-mode.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { Subscription } from 'rxjs';
import { LoginModalService } from 'src/app/services/login-modal/login-modal.service';

@Component({
  selector: 'app-header-component',
  templateUrl: './header-component.component.html',
  styleUrls: ['./header-component.component.css'],
})
export class HeaderComponentComponent implements OnInit, OnDestroy {
  menuItems: MenuItem[] = [];
  private isSmallScreen = false;
  private isBrowser: boolean;
  isAppMenuOpen = false;
  showAppLangOptions = false;
  instaUrl: string = '';
  facebookUrl: string = '';
  xUrl: string = '';
  whatsappUrl: string = '';
  whatsappNumber: string = '';
  email: string = '';
  showLoginModal = false;
  loginEmail = '';
  loginPassword = '';
  loginError = '';
  private loginModalSub?: Subscription;

  availableLangs = [
    { code: 'en', label: 'EN', flag: 'gb' },
    { code: 'fr', label: 'FR', flag: 'fr' },
    { code: 'ar', label: 'AR', flag: 'sa' },
  ];
  selectedLang = 'en';
  showLangDropdown = false;
  showCurrencyDropdown = false;

  currencyOptions = ['EGP', 'USD', 'SAR'];
  currencySymbols: Record<string, string> =
    this.currencyService.currencySymbols;
  selectedCurrency: string = '';

  catalogueUrl: string =
    environment.apiUrl + '/dashboard/home-config/download-catalogue';
  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly configService: HomeService,
    private readonly currencyService: CurrencySelectionService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    public readonly appModeService: AppModeService,
    public readonly authService: AuthService,
    private readonly loginModalService: LoginModalService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.translate.addLangs(this.availableLangs.map((l) => l.code));
    const savedLang = localStorage.getItem('lang') || 'en';
    this.translate.setDefaultLang('en');
    this.translate.use(savedLang);
    this.selectedLang = savedLang;
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.checkScreenSize();
      this.buildMenuItems();
    }
    this.loginModalSub = this.loginModalService.open$.subscribe(() => {
      this.openLoginModal();
    });

    this.selectedCurrency = this.currencyService.getSelectedCurrency();

    this.configService.getConfig('en').subscribe((data) => {
      this.facebookUrl = data.facebookUrl;
      this.instaUrl = data.instaUrl;
      this.whatsappUrl = data.whatsappUrl;
      this.xUrl = data.xUrl;
      this.email = data.email;
      this.whatsappNumber = this.extractFormattedPhone(data.whatsappUrl);
    });
    this.translate.addLangs(this.availableLangs.map((l) => l.code));
    const savedLang = localStorage.getItem('lang') || 'en';
    this.translate.setDefaultLang('en');
    this.translate.use(savedLang);
    this.selectedLang = savedLang;
  }

  ngOnDestroy(): void {
    this.loginModalSub?.unsubscribe();
  }

  onLanguageChange(lang: string) {
    this.selectedLang = lang;
    this.translate.use(lang);
    localStorage.setItem('lang', lang);
    this.showLangDropdown = false;

    const segments = this.router.url.split('/');
    segments[1] = lang;
    const newUrl = segments.join('/');
    this.router.navigateByUrl(newUrl);
    setTimeout(() => {
      window.location.reload();
    });
  }

  openAppMenu(): void {
    this.isAppMenuOpen = true;
    this.showAppLangOptions = false;
  }

  closeAppMenu(): void {
    this.isAppMenuOpen = false;
    this.showAppLangOptions = false;
  }

  openLoginModal(): void {
    this.showLoginModal = true;
    this.loginError = '';
    this.loginPassword = '';
  }

  closeLoginModal(): void {
    this.showLoginModal = false;
    this.loginError = '';
  }

  submitLogin(): void {
    this.loginError = '';
    this.authService.login(this.loginEmail, this.loginPassword).subscribe({
      next: () => {
        this.closeLoginModal();
        this.closeAppMenu();
      },
      error: (error) => {
        this.loginError = error?.error?.message || 'Invalid email or password';
      },
    });
  }

  navigateToProfile(): void {
    // TODO: remove unused method
    const lang = localStorage.getItem('lang') || 'en';
    this.closeAppMenu();
    this.router.navigate([`/${lang}/profile`], { queryParamsHandling: 'preserve' });
  }

  navigateFromAppMenu(path: string): void {
    const lang = localStorage.getItem('lang') || 'en';
    this.closeAppMenu();
    this.router.navigate([`/${lang}/${path}`], {
      queryParamsHandling: 'preserve',
    });
  }

  toggleAppLanguages(): void {
    this.showAppLangOptions = !this.showAppLangOptions;
  }

  onAppLanguageSelect(lang: string): void {
    this.showAppLangOptions = false;
    this.closeAppMenu();
    this.onLanguageChange(lang);
  }

  private extractFormattedPhone(url: string): string {
    try {
      const decodedUrl = decodeURIComponent(url);
      const match = decodedUrl.match(/wa\.me\/(\d{11,})/);

      if (!match?.[1]) {
        return '';
      }

      const raw = match[1];
      if (raw.startsWith('20') && raw.length === 12) {
        const countryCode = '+20';
        const operator = raw.slice(2, 4);
        const rest = raw.slice(4);
        return `${countryCode} ${operator} ${rest.slice(0, 3)} ${rest.slice(3)}`;
      }

      return raw;
    } catch {
      return '';
    }
  }

  get currentLangFlag(): string {
    const lang = this.availableLangs.find((l) => l.code === this.selectedLang);
    return lang ? 'fi-' + lang.flag : '';
  }

  isActiveLang(lang: string) {
    return this.selectedLang === lang;
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this.isBrowser) {
      const previousIsSmallScreen = this.isSmallScreen;
      this.checkScreenSize();
      if (previousIsSmallScreen !== this.isSmallScreen) {
        this.buildMenuItems();
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;

    const clickedInsideCurrency = target.closest('.currency-dropdown');
    const clickedInsideLang = target.closest('.lang-dropdown');

    if (!clickedInsideCurrency) {
      this.showCurrencyDropdown = false;
    }

    if (!clickedInsideLang) {
      this.showLangDropdown = false;
    }
  }

  toggleCurrencyDropdown(event: MouseEvent) {
    event.stopPropagation();
    this.showCurrencyDropdown = !this.showCurrencyDropdown;
    this.showLangDropdown = false;
  }

  toggleLangDropdown(event: MouseEvent) {
    event.stopPropagation();
    this.showLangDropdown = !this.showLangDropdown;
    this.showCurrencyDropdown = false;
  }

  onCurrencyChange(currency: string) {
    this.selectedCurrency = currency;
    this.currencyService.setSelectedCurrency(currency);
    this.showCurrencyDropdown = false;
  }

  private checkScreenSize() {
    this.isSmallScreen = window.innerWidth < 768;
  }

  private buildMenuItems() {
    this.translate
      .stream([
        'MENU.HOME',
        'MENU.PRODUCTS',
        'MENU.SHOES',
        'MENU.TOPWEAR',
        'MENU.CUSTOMIZE',
        'MENU.BLOGS',
        'MENU.DOWNLOAD_CATALOGUE',
      ])
      .subscribe((translations) => {
        this.menuItems = [
          {
            label: translations['MENU.HOME'],
            routerLink: '/home',
            routerLinkActiveOptions: { exact: true },
          },
          { label: translations['MENU.PRODUCTS'], routerLink: '/products' },
          {
            label: translations['MENU.SHOES'],
            routerLink: '/products',
            queryParams: { category: 'running%20shoes' },
          },
          {
            label: translations['MENU.TOPWEAR'],
            routerLink: '/products',
            queryParams: { category: ['jersies'] },
          },
          {
            label: translations['MENU.CUSTOMIZE'],
            routerLink: '/customization',
          },
          { label: translations['MENU.BLOGS'], routerLink: '/blogs' },
        ];

        if (this.isSmallScreen) {
          this.menuItems.push({
            label: translations['MENU.DOWNLOAD_CATALOGUE'],
            icon: 'pi pi-download',
            command: () => {
              if (this.isBrowser) {
                const link = document.createElement('a');
                link.href = this.catalogueUrl;
                link.download = 'wemble sport catalog.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            },
          });
        }
      });
  }
}
